import React from "react";

export type SetActions<T> = {
  add: (value: T) => void;
  remove: (value: T) => void;
  toggle: (value: T) => void;
  clear: () => void;
  reset: (values?: Iterable<T> | null) => void;
};

export function useSet<T>(initial?: Iterable<T> | null): readonly [ReadonlySet<T>, SetActions<T>] {
  const [set, setSet] = React.useState<Set<T>>(() => new Set<T>(initial ?? []));
  const actions = React.useMemo<SetActions<T>>(
    () => ({
      add: (value) =>
        setSet((prev) => {
          if (prev.has(value)) return prev;
          const next = new Set(prev);
          next.add(value);
          return next;
        }),
      remove: (value) =>
        setSet((prev) => {
          if (!prev.has(value)) return prev;
          const next = new Set(prev);
          next.delete(value);
          return next;
        }),
      toggle: (value) =>
        setSet((prev) => {
          const next = new Set(prev);
          if (next.has(value)) next.delete(value);
          else next.add(value);
          return next;
        }),
      clear: () => setSet((prev) => (prev.size === 0 ? prev : new Set())),
      reset: (values) => setSet(new Set(values ?? [])),
    }),
    [],
  );
  return [set, actions];
}
